/**
 * WILLOW & DOVE — Discover / Doom Scroll
 * Zara-inspired infinite scroll with touch-to-reveal pricing
 *
 * Features:
 * - Infinite scroll via IntersectionObserver + Shopify section rendering API
 * - Touch-to-reveal pricing (touchstart → reveal, touchend → fade after 2s)
 * - Category tab filtering (switches collection)
 * - Lazy image loading via IntersectionObserver
 */

(function () {
  'use strict';

  /* ========== STATE ========== */
  const state = {
    currentCollection: 'all',
    currentPage: 1,
    isLoading: false,
    hasMore: true,
    productsPerPage: 20,
    revealTimers: new WeakMap()
  };

  /* ========== DOM REFERENCES ========== */
  const feed = document.getElementById('discover-feed');
  const loader = document.getElementById('discover-loader');
  const sectionId = feed ? feed.dataset.sectionId : 'doom-scroll';

  if (!feed || !loader) return;

  /* ========== CATEGORY TABS ========== */
  const tabs = document.querySelectorAll('.discover-tabs__tab');

  tabs.forEach(function (tab) {
    tab.addEventListener('click', function () {
      var collection = this.dataset.collection;
      if (collection === state.currentCollection && state.currentPage > 1) return;

      // Update active state
      tabs.forEach(function (t) { t.classList.remove('active'); });
      this.classList.add('active');

      // Reset and reload
      state.currentCollection = collection;
      state.currentPage = 1;
      state.hasMore = true;

      // Clear feed (keep only skeleton placeholders)
      feed.innerHTML = '';
      showLoader();
      loadNextPage();
    });
  });

  /* ========== INFINITE SCROLL OBSERVER ========== */
  var scrollObserver = new IntersectionObserver(function (entries) {
    entries.forEach(function (entry) {
      if (entry.isIntersecting && !state.isLoading && state.hasMore) {
        loadNextPage();
      }
    });
  }, {
    rootMargin: '600px'  // Start loading well before user reaches bottom
  });

  scrollObserver.observe(loader);

  /* ========== LAZY IMAGE OBSERVER ========== */
  var imageObserver = new IntersectionObserver(function (entries) {
    entries.forEach(function (entry) {
      if (entry.isIntersecting) {
        var img = entry.target;
        if (img.dataset.src) {
          img.src = img.dataset.src;
          img.removeAttribute('data-src');
        }
        if (img.dataset.srcset) {
          img.srcset = img.dataset.srcset;
          img.removeAttribute('data-srcset');
        }
        img.addEventListener('load', function () {
          var item = img.closest('.discover-item');
          if (item) item.dataset.loaded = 'true';
        }, { once: true });
        imageObserver.unobserve(img);
      }
    });
  }, {
    rootMargin: '400px'
  });

  /* ========== LOAD NEXT PAGE ========== */
  function loadNextPage() {
    if (state.isLoading || !state.hasMore) return;
    state.isLoading = true;
    showLoader();

    var url = '/collections/' + state.currentCollection
      + '?section_id=' + sectionId
      + '&page=' + (state.currentPage + 1);

    fetch(url)
      .then(function (res) {
        if (!res.ok) throw new Error('HTTP ' + res.status);
        return res.text();
      })
      .then(function (html) {
        // Parse the returned HTML to extract product items
        var parser = new DOMParser();
        var doc = parser.parseFromString(html, 'text/html');
        var newItems = doc.querySelectorAll('.discover-item');

        if (newItems.length === 0) {
          state.hasMore = false;
          hideLoader();
          showEndMessage();
          return;
        }

        // Append new items to feed
        newItems.forEach(function (item) {
          item.dataset.loaded = 'false';
          feed.appendChild(item);

          // Observe images for lazy loading
          var img = item.querySelector('.discover-item__image');
          if (img && img.dataset.src) {
            imageObserver.observe(img);
          } else if (img) {
            // Image already has src, mark as loaded
            item.dataset.loaded = 'true';
          }

          // Bind touch events
          bindTouchReveal(item);
        });

        state.currentPage++;
        state.isLoading = false;

        // If we got fewer items than expected, probably last page
        if (newItems.length < state.productsPerPage) {
          state.hasMore = false;
          hideLoader();
          showEndMessage();
        } else {
          hideLoader();
        }
      })
      .catch(function (err) {
        console.error('[Discover] Failed to load page:', err);
        state.isLoading = false;
        hideLoader();
      });
  }

  /* ========== TOUCH-TO-REVEAL PRICING ========== */
  function bindTouchReveal(item) {
    var priceWrap = item.querySelector('.discover-item__price-wrap');
    if (!priceWrap) return;

    var touchTimer = null;
    var hideTimer = null;

    // Touch start — reveal price after short delay (200ms)
    item.addEventListener('touchstart', function (e) {
      // Clear any pending hide
      if (hideTimer) {
        clearTimeout(hideTimer);
        hideTimer = null;
      }

      touchTimer = setTimeout(function () {
        priceWrap.classList.add('revealed');
      }, 200);
    }, { passive: true });

    // Touch end — keep visible for 2s, then fade out
    item.addEventListener('touchend', function (e) {
      if (touchTimer) {
        clearTimeout(touchTimer);
        touchTimer = null;
      }

      // Reveal immediately if not already (for quick taps)
      priceWrap.classList.add('revealed');

      hideTimer = setTimeout(function () {
        priceWrap.classList.remove('revealed');
        hideTimer = null;
      }, 2000);
    }, { passive: true });

    // Touch cancel — hide price
    item.addEventListener('touchcancel', function () {
      if (touchTimer) {
        clearTimeout(touchTimer);
        touchTimer = null;
      }
      priceWrap.classList.remove('revealed');
    }, { passive: true });
  }

  /* ========== INIT: BIND EVENTS ON INITIAL ITEMS ========== */
  var initialItems = feed.querySelectorAll('.discover-item');
  initialItems.forEach(function (item) {
    bindTouchReveal(item);

    // Observe images
    var img = item.querySelector('.discover-item__image');
    if (img && img.dataset.src) {
      imageObserver.observe(img);
    } else if (img) {
      item.dataset.loaded = 'true';
    }
  });

  /* ========== LOADER HELPERS ========== */
  function showLoader() {
    loader.classList.add('visible');
  }

  function hideLoader() {
    loader.classList.remove('visible');
  }

  function showEndMessage() {
    // Only add if not already present
    if (feed.querySelector('.discover-end')) return;
    var endMsg = document.createElement('div');
    endMsg.className = 'discover-end';
    endMsg.textContent = 'You\u2019ve seen it all';
    feed.appendChild(endMsg);
  }

  /* ========== SCROLL-AWARE TAB BAR ========== */
  var tabBar = document.querySelector('.discover-tabs');
  if (tabBar) {
    var lastScrollY = 0;
    var ticking = false;

    window.addEventListener('scroll', function () {
      lastScrollY = window.scrollY;
      if (!ticking) {
        window.requestAnimationFrame(function () {
          // Add subtle shadow when scrolled
          if (lastScrollY > 10) {
            tabBar.style.boxShadow = '0 1px 4px rgba(0,0,0,0.06)';
          } else {
            tabBar.style.boxShadow = 'none';
          }
          ticking = false;
        });
        ticking = true;
      }
    }, { passive: true });
  }

})();
